import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Target,
  TrendingUp, 
  Users, 
  DollarSign, 
  ArrowRight, 
  CheckCircle2,
  Zap,
  AlertTriangle,
  Eye,
  Shield,
  Award,
  Scale,
  Briefcase,
  MessageSquare,
  Search,
  Sparkles,
  Save,
  XCircle,
  Star,
  Crown,
  Megaphone,
  UserCheck,
  BadgeCheck,
  CircleDollarSign
} from 'lucide-react';

const ValueCaptureModule = ({ onClose, onSavePlan }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  
  const [currentScreen, setCurrentScreen] = useState(0);
  const [selfAssessment, setSelfAssessment] = useState({
    negotiates: null,
    understandsMarketValue: null,
    easilyReplaceable: null,
    valueRecognized: null,
    activelyPositions: null
  });
  const [capturePlan, setCapturePlan] = useState({
    underCaptureArea: null,
    actions: []
  });

  // Content translations
  const content = {
    en: {
      // Slide 1 - Title
      slide1: {
        title: "Value Capture",
        message: "Creating value is not enough.",
        highlight: "You must also learn to capture a fair share of the value you create.",
        button: "Start Learning",
        time: "8-10 min"
      },
      // Slide 2 - The Value Capture Problem
      slide2: {
        title: "The Value Capture Problem",
        intro: "Many people work hard, generate value, and help companies or clients succeed...",
        problem: "But their income does not reflect the value they create.",
        insight: "The market does not automatically reward value.",
        solution: "You must position yourself to capture it.",
        button: "Continue"
      },
      // Slide 3 - Why People Fail to Capture Value
      slide3: {
        title: "Why People Fail to Capture Value",
        intro: "The most common reasons:",
        reasons: [
          { icon: "MessageSquare", label: "Poor negotiation skills", desc: "Not asking for what they deserve" },
          { icon: "Eye", label: "Weak positioning", desc: "Not standing out in the market" },
          { icon: "XCircle", label: "Undervaluing contribution", desc: "Not recognizing their own worth" },
          { icon: "Search", label: "Lack of visibility", desc: "Nobody knows what they can do" },
          { icon: "DollarSign", label: "No pricing power", desc: "Accepting whatever is offered" },
          { icon: "AlertTriangle", label: "Accepting first offers", desc: "Not negotiating at all" }
        ],
        button: "Continue"
      },
      // Slide 4 - The Value Capture Formula
      slide4: {
        title: "The Value Capture Formula",
        intro: "Your income depends on three elements:",
        elements: [
          { name: "Value You Create", desc: "The actual contribution you make", icon: "Zap", color: "yellow" },
          { name: "Market Demand", desc: "How much the market wants what you offer", icon: "TrendingUp", color: "blue" },
          { name: "Negotiation & Positioning", desc: "Your ability to capture that value", icon: "Target", color: "green" }
        ],
        insight: "Most people focus only on working harder instead of improving the last two.",
        button: "Continue"
      },
      // Slide 5 - The Power of Positioning
      slide5: {
        title: "The Power of Positioning",
        intro: "Perceived value strongly influences compensation.",
        example: "Two lawyers with similar skills can earn very different amounts depending on:",
        factors: [
          "Reputation",
          "Specialization",
          "Client portfolio",
          "Visibility"
        ],
        insight: "Positioning can dramatically change income without increasing working hours.",
        button: "Continue"
      },
      // Slide 6 - Market Value vs Replacement Cost
      slide6: {
        title: "Market Value vs Replacement Cost",
        concepts: [
          {
            name: "Market Value",
            desc: "How much the market is willing to pay for your contribution.",
            icon: "CircleDollarSign",
            color: "green"
          },
          {
            name: "Replacement Cost",
            desc: "How easy or difficult it is to replace you.",
            icon: "UserCheck",
            color: "purple"
          }
        ],
        insight: "People who are hard to replace capture more value.",
        examples: "A specialized surgeon vs a general practitioner. A senior engineer vs a junior developer.",
        button: "Continue"
      },
      // Slide 7 - Cost of Not Having You
      slide7: {
        title: "The Cost of Not Having You",
        intro: "Sometimes your value comes from the cost of NOT having your contribution.",
        examples: [
          { role: "Cybersecurity Expert", value: "Preventing costly attacks" },
          { role: "Good Manager", value: "Avoiding expensive mistakes" },
          { role: "Experienced Lawyer", value: "Preventing legal disasters" },
          { role: "Quality Engineer", value: "Avoiding product failures" }
        ],
        insight: "This perspective helps you understand your hidden value.",
        button: "Continue"
      },
      // Slide 8 - Pricing Power
      slide8: {
        title: "Pricing Power",
        intro: "The ability to set or influence the price of your work.",
        subtitle: "Sources of pricing power:",
        sources: [
          { name: "Specialization", desc: "Deep expertise in a niche" },
          { name: "Reputation", desc: "Proven track record" },
          { name: "Scarcity", desc: "Few people can do what you do" },
          { name: "Brand", desc: "Recognition and trust" },
          { name: "Results", desc: "Demonstrated outcomes" }
        ],
        button: "Continue"
      },
      // Slide 9 - Self Assessment
      slide9: {
        title: "Value Capture Self-Assessment",
        intro: "Reflect on your current situation:",
        questions: [
          { id: "negotiates", text: "Do you negotiate your salary or fees?" },
          { id: "understandsMarketValue", text: "Do you understand the market value of your skills?" },
          { id: "easilyReplaceable", text: "Would you be difficult to replace in your current role?" },
          { id: "valueRecognized", text: "Do people recognize the value you provide?" },
          { id: "activelyPositions", text: "Do you actively position yourself in the market?" }
        ],
        options: [
          { value: 1, label: "Not at all" },
          { value: 2, label: "Rarely" },
          { value: 3, label: "Sometimes" },
          { value: 4, label: "Often" },
          { value: 5, label: "Always" }
        ],
        note: "This is a reflection tool, not a test.",
        button: "Continue"
      },
      // Slide 10 - Improving Your Value Capture
      slide10: {
        title: "Improving Your Value Capture",
        intro: "Practical strategies to capture more value:",
        strategies: [
          { icon: "MessageSquare", text: "Improve negotiation skills" },
          { icon: "Megaphone", text: "Increase visibility and reputation" },
          { icon: "Award", text: "Develop specialized expertise" },
          { icon: "Target", text: "Choose better markets or industries" },
          { icon: "Eye", text: "Communicate your value clearly" },
          { icon: "TrendingUp", text: "Move to environments where your value is recognized" }
        ],
        button: "Continue"
      },
      // Slide 11 - Build Your Plan
      slide11: {
        title: "Build Your Value Capture Plan",
        intro: "Identify where you currently under-capture value:",
        areaLabel: "Where do you under-capture value?",
        areas: [
          { id: "salary", label: "Salary", desc: "Not paid what you're worth" },
          { id: "freelance", label: "Freelance pricing", desc: "Charging too little" },
          { id: "business", label: "Business pricing", desc: "Underpricing products/services" },
          { id: "promotion", label: "Promotion opportunities", desc: "Overlooked for advancement" }
        ],
        actionsLabel: "Actions to improve:",
        actions: [
          { id: "negotiate", label: "Improve negotiation skills" },
          { id: "research", label: "Research market salaries" },
          { id: "reposition", label: "Reposition my skills" },
          { id: "visibility", label: "Increase visibility" },
          { id: "changeMarket", label: "Change market or clients" },
          { id: "specialize", label: "Develop specialization" }
        ],
        button: "See Summary"
      },
      // Slide 12 - Final
      slide12: {
        title: "The Path Forward",
        message: "People are rarely paid exactly what they deserve.",
        highlight: "They are paid according to how well they position and negotiate the value they create.",
        summary: "Your value capture plan is ready. Take action today.",
        saveButton: "Save My Value Capture Plan",
        finishButton: "Finish"
      }
    },
    es: {
      // Slide 1 - Title
      slide1: {
        title: "Captura de Valor",
        message: "Crear valor no es suficiente.",
        highlight: "También debes aprender a capturar una parte justa del valor que creas.",
        button: "Comenzar",
        time: "8-10 min"
      },
      // Slide 2 - The Value Capture Problem
      slide2: {
        title: "El Problema de la Captura de Valor",
        intro: "Muchas personas trabajan duro, generan valor y ayudan a empresas o clientes a tener éxito...",
        problem: "Pero sus ingresos no reflejan el valor que crean.",
        insight: "El mercado no recompensa automáticamente el valor.",
        solution: "Debes posicionarte para capturarlo.",
        button: "Continuar"
      },
      // Slide 3 - Why People Fail to Capture Value
      slide3: {
        title: "Por Qué las Personas No Capturan Valor",
        intro: "Las razones más comunes:",
        reasons: [
          { icon: "MessageSquare", label: "Pobres habilidades de negociación", desc: "No pedir lo que merecen" },
          { icon: "Eye", label: "Posicionamiento débil", desc: "No destacar en el mercado" },
          { icon: "XCircle", label: "Subestimar su contribución", desc: "No reconocer su propio valor" },
          { icon: "Search", label: "Falta de visibilidad", desc: "Nadie sabe lo que pueden hacer" },
          { icon: "DollarSign", label: "Sin poder de precio", desc: "Aceptar lo que les ofrecen" },
          { icon: "AlertTriangle", label: "Aceptar primeras ofertas", desc: "No negociar en absoluto" }
        ],
        button: "Continuar"
      },
      // Slide 4 - The Value Capture Formula
      slide4: {
        title: "La Fórmula de Captura de Valor",
        intro: "Tu ingreso depende de tres elementos:",
        elements: [
          { name: "Valor que Creas", desc: "La contribución real que haces", icon: "Zap", color: "yellow" },
          { name: "Demanda del Mercado", desc: "Cuánto el mercado quiere lo que ofreces", icon: "TrendingUp", color: "blue" },
          { name: "Negociación y Posicionamiento", desc: "Tu capacidad de capturar ese valor", icon: "Target", color: "green" }
        ],
        insight: "La mayoría de las personas solo se enfocan en trabajar más duro en lugar de mejorar los últimos dos.",
        button: "Continuar"
      },
      // Slide 5 - The Power of Positioning
      slide5: {
        title: "El Poder del Posicionamiento",
        intro: "El valor percibido influye fuertemente en la compensación.",
        example: "Dos abogados con habilidades similares pueden ganar cantidades muy diferentes dependiendo de:",
        factors: [
          "Reputación",
          "Especialización",
          "Cartera de clientes",
          "Visibilidad"
        ],
        insight: "El posicionamiento puede cambiar dramáticamente los ingresos sin aumentar las horas de trabajo.",
        button: "Continuar"
      },
      // Slide 6 - Market Value vs Replacement Cost
      slide6: {
        title: "Valor de Mercado vs Costo de Reemplazo",
        concepts: [
          {
            name: "Valor de Mercado",
            desc: "Cuánto el mercado está dispuesto a pagar por tu contribución.",
            icon: "CircleDollarSign",
            color: "green"
          },
          {
            name: "Costo de Reemplazo",
            desc: "Qué tan fácil o difícil es reemplazarte.",
            icon: "UserCheck",
            color: "purple"
          }
        ],
        insight: "Las personas difíciles de reemplazar capturan más valor.",
        examples: "Un cirujano especializado vs un médico general. Un ingeniero senior vs un desarrollador junior.",
        button: "Continuar"
      },
      // Slide 7 - Cost of Not Having You
      slide7: {
        title: "El Costo de No Tenerte",
        intro: "A veces tu valor viene del costo de NO tener tu contribución.",
        examples: [
          { role: "Experto en Ciberseguridad", value: "Prevenir ataques costosos" },
          { role: "Buen Gerente", value: "Evitar errores caros" },
          { role: "Abogado Experimentado", value: "Prevenir desastres legales" },
          { role: "Ingeniero de Calidad", value: "Evitar fallas de producto" }
        ],
        insight: "Esta perspectiva te ayuda a entender tu valor oculto.",
        button: "Continuar"
      },
      // Slide 8 - Pricing Power
      slide8: {
        title: "Poder de Precio",
        intro: "La capacidad de establecer o influir en el precio de tu trabajo.",
        subtitle: "Fuentes de poder de precio:",
        sources: [
          { name: "Especialización", desc: "Experiencia profunda en un nicho" },
          { name: "Reputación", desc: "Historial comprobado" },
          { name: "Escasez", desc: "Pocas personas pueden hacer lo que tú haces" },
          { name: "Marca", desc: "Reconocimiento y confianza" },
          { name: "Resultados", desc: "Resultados demostrados" }
        ],
        button: "Continuar"
      },
      // Slide 9 - Self Assessment
      slide9: {
        title: "Autoevaluación de Captura de Valor",
        intro: "Reflexiona sobre tu situación actual:",
        questions: [
          { id: "negotiates", text: "¿Negocias tu salario o tarifas?" },
          { id: "understandsMarketValue", text: "¿Entiendes el valor de mercado de tus habilidades?" },
          { id: "easilyReplaceable", text: "¿Serías difícil de reemplazar en tu rol actual?" },
          { id: "valueRecognized", text: "¿Las personas reconocen el valor que aportas?" },
          { id: "activelyPositions", text: "¿Te posicionas activamente en el mercado?" }
        ],
        options: [
          { value: 1, label: "Nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "A veces" },
          { value: 4, label: "Frecuentemente" },
          { value: 5, label: "Siempre" }
        ],
        note: "Esta es una herramienta de reflexión, no un examen.",
        button: "Continuar"
      },
      // Slide 10 - Improving Your Value Capture
      slide10: {
        title: "Mejorando Tu Captura de Valor",
        intro: "Estrategias prácticas para capturar más valor:",
        strategies: [
          { icon: "MessageSquare", text: "Mejorar habilidades de negociación" },
          { icon: "Megaphone", text: "Aumentar visibilidad y reputación" },
          { icon: "Award", text: "Desarrollar experiencia especializada" },
          { icon: "Target", text: "Elegir mejores mercados o industrias" },
          { icon: "Eye", text: "Comunicar tu valor claramente" },
          { icon: "TrendingUp", text: "Moverse a entornos donde tu valor sea reconocido" }
        ],
        button: "Continuar"
      },
      // Slide 11 - Build Your Plan
      slide11: {
        title: "Construye Tu Plan de Captura de Valor",
        intro: "Identifica dónde actualmente no capturas suficiente valor:",
        areaLabel: "¿Dónde no capturas suficiente valor?",
        areas: [
          { id: "salary", label: "Salario", desc: "No te pagan lo que vales" },
          { id: "freelance", label: "Precios freelance", desc: "Cobras muy poco" },
          { id: "business", label: "Precios de negocio", desc: "Subvaloras productos/servicios" },
          { id: "promotion", label: "Oportunidades de promoción", desc: "Te pasan por alto para ascensos" }
        ],
        actionsLabel: "Acciones para mejorar:",
        actions: [
          { id: "negotiate", label: "Mejorar habilidades de negociación" },
          { id: "research", label: "Investigar salarios del mercado" },
          { id: "reposition", label: "Reposicionar mis habilidades" },
          { id: "visibility", label: "Aumentar visibilidad" },
          { id: "changeMarket", label: "Cambiar mercado o clientes" },
          { id: "specialize", label: "Desarrollar especialización" }
        ],
        button: "Ver Resumen"
      },
      // Slide 12 - Final
      slide12: {
        title: "El Camino Adelante",
        message: "Las personas rara vez reciben exactamente lo que merecen.",
        highlight: "Reciben según qué tan bien posicionan y negocian el valor que crean.",
        summary: "Tu plan de captura de valor está listo. Toma acción hoy.",
        saveButton: "Guardar Mi Plan de Captura de Valor",
        finishButton: "Finalizar"
      }
    }
  };

  const c = content[lang];

  const handleAssessmentChange = (questionId, value) => {
    setSelfAssessment(prev => ({ ...prev, [questionId]: value }));
  };

  const handlePlanChange = (field, value) => {
    setCapturePlan(prev => ({ ...prev, [field]: value }));
  };

  const toggleAction = (actionId) => {
    setCapturePlan(prev => ({
      ...prev,
      actions: prev.actions.includes(actionId)
        ? prev.actions.filter(a => a !== actionId)
        : [...prev.actions, actionId]
    }));
  };

  const savePlan = async () => {
    const plan = {
      selfAssessment,
      capturePlan,
      createdAt: new Date().toISOString()
    };
    
    if (onSavePlan) {
      await onSavePlan(plan);
    }
  };

  const nextScreen = () => setCurrentScreen(prev => prev + 1);

  const handleFinish = () => {
    if (onClose) onClose();
  };

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };

  // Icon component helper
  const IconComponent = ({ name, className }) => {
    const icons = {
      Zap, TrendingUp, Users, DollarSign, Target, AlertTriangle, Eye, Shield,
      Award, Scale, Briefcase, MessageSquare, Search, Sparkles, XCircle, Star,
      Crown, Megaphone, UserCheck, BadgeCheck, CircleDollarSign
    };
    const Icon = icons[name];
    return Icon ? <Icon className={className} /> : null;
  };

  // Slide 1 - Title
  const renderSlide1 = () => (
    <motion.div 
      key="slide1"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
        <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
          <Target className="w-7 h-7 text-white" />
        </div>
        
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">{c.slide1.title}</h1>
        
        <div className="space-y-2 mb-4 max-w-sm">
          <p className="text-gray-300 text-sm">{c.slide1.message}</p>
          <p className="text-emerald-400 font-semibold text-sm">{c.slide1.highlight}</p>
        </div>
        
        <p className="text-gray-500 text-xs mt-2">{c.slide1.time}</p>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-teal-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide1.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 2 - The Value Capture Problem
  const renderSlide2 = () => (
    <motion.div 
      key="slide2"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center mb-3">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
        </div>
        
        <h1 className="text-lg md:text-xl font-bold text-white mb-3 text-center">{c.slide2.title}</h1>
        
        <p className="text-gray-300 text-sm mb-2 text-center max-w-sm">{c.slide2.intro}</p>
        
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 mb-4 max-w-sm">
          <p className="text-red-400 text-sm text-center font-medium">{c.slide2.problem}</p>
        </div>
        
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 max-w-sm">
          <p className="text-gray-300 text-sm text-center mb-1">{c.slide2.insight}</p>
          <p className="text-emerald-400 text-sm text-center font-semibold">{c.slide2.solution}</p>
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-teal-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide2.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 3 - Why People Fail to Capture Value
  const renderSlide3 = () => (
    <motion.div 
      key="slide3"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center px-4 py-3 overflow-y-auto">
        <h1 className="text-lg md:text-xl font-bold text-white mb-1 text-center">{c.slide3.title}</h1>
        <p className="text-gray-400 text-xs mb-3 text-center">{c.slide3.intro}</p>
        
        <div className="grid grid-cols-2 gap-2 w-full max-w-sm">
          {c.slide3.reasons.map((reason, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="bg-gray-800/50 border border-gray-700 rounded-lg p-2.5"
            >
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <IconComponent name={reason.icon} className="w-3.5 h-3.5 text-red-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-xs font-medium leading-tight">{reason.label}</p>
                  <p className="text-gray-500 text-xs leading-tight mt-0.5">{reason.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-teal-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide3.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 4 - The Value Capture Formula
  const renderSlide4 = () => (
    <motion.div 
      key="slide4"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        <h1 className="text-lg md:text-xl font-bold text-white mb-2 text-center">{c.slide4.title}</h1>
        <p className="text-gray-400 text-xs mb-4 text-center">{c.slide4.intro}</p>
        
        <div className="space-y-2.5 w-full max-w-sm mb-4">
          {c.slide4.elements.map((element, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.15 }}
              className={`bg-${element.color}-500/10 border border-${element.color}-500/30 rounded-lg p-3`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${element.color}-500/20`}>
                  <IconComponent name={element.icon} className={`w-5 h-5 text-${element.color}-500`} />
                </div>
                <div>
                  <p className={`text-${element.color}-400 font-semibold text-sm`}>{element.name}</p>
                  <p className="text-gray-400 text-xs">{element.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="bg-gray-800/30 rounded-lg p-2 max-w-sm">
          <p className="text-gray-300 text-xs text-center">{c.slide4.insight}</p>
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-teal-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide4.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 5 - The Power of Positioning
  const renderSlide5 = () => (
    <motion.div 
      key="slide5"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center mb-3">
          <Crown className="w-5 h-5 text-purple-500" />
        </div>
        
        <h1 className="text-lg md:text-xl font-bold text-white mb-2 text-center">{c.slide5.title}</h1>
        <p className="text-gray-300 text-sm mb-3 text-center max-w-sm">{c.slide5.intro}</p>
        
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 mb-3 max-w-sm w-full">
          <p className="text-gray-300 text-xs mb-2">{c.slide5.example}</p>
          <div className="flex flex-wrap gap-1.5">
            {c.slide5.factors.map((factor, i) => (
              <span key={i} className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded text-xs">
                {factor}
              </span>
            ))}
          </div>
        </div>
        
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2 max-w-sm">
          <p className="text-emerald-400 text-xs text-center font-medium">{c.slide5.insight}</p>
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-teal-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide5.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 6 - Market Value vs Replacement Cost
  const renderSlide6 = () => (
    <motion.div 
      key="slide6"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        <h1 className="text-lg md:text-xl font-bold text-white mb-3 text-center">{c.slide6.title}</h1>
        
        <div className="space-y-3 w-full max-w-sm mb-3">
          {c.slide6.concepts.map((concept, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.15 }}
              className={`bg-${concept.color}-500/10 border border-${concept.color}-500/30 rounded-lg p-3`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${concept.color}-500/20 flex-shrink-0`}>
                  <IconComponent name={concept.icon} className={`w-5 h-5 text-${concept.color}-500`} />
                </div>
                <div>
                  <p className={`text-${concept.color}-400 font-semibold text-sm`}>{concept.name}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{concept.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2 mb-2 max-w-sm">
          <p className="text-emerald-400 text-xs text-center font-medium">{c.slide6.insight}</p>
        </div>
        
        <p className="text-gray-500 text-xs text-center max-w-sm italic">{c.slide6.examples}</p>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-teal-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide6.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 7 - Cost of Not Having You
  const renderSlide7 = () => (
    <motion.div 
      key="slide7"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center mb-3">
          <Shield className="w-5 h-5 text-blue-500" />
        </div>
        
        <h1 className="text-lg md:text-xl font-bold text-white mb-2 text-center">{c.slide7.title}</h1>
        <p className="text-gray-300 text-sm mb-3 text-center max-w-sm">{c.slide7.intro}</p>
        
        <div className="grid grid-cols-2 gap-2 w-full max-w-sm mb-3">
          {c.slide7.examples.map((example, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 }}
              className="bg-gray-800/50 border border-gray-700 rounded-lg p-2.5"
            >
              <p className="text-blue-400 text-xs font-medium mb-0.5">{example.role}</p>
              <p className="text-gray-400 text-xs">{example.value}</p>
            </motion.div>
          ))}
        </div>
        
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 max-w-sm">
          <p className="text-blue-400 text-xs text-center">{c.slide7.insight}</p>
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-teal-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide7.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 8 - Pricing Power
  const renderSlide8 = () => (
    <motion.div 
      key="slide8"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center px-4 py-3 overflow-y-auto">
        <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center mb-2">
          <DollarSign className="w-5 h-5 text-yellow-500" />
        </div>
        
        <h1 className="text-lg md:text-xl font-bold text-white mb-1 text-center">{c.slide8.title}</h1>
        <p className="text-gray-300 text-xs mb-2 text-center max-w-sm">{c.slide8.intro}</p>
        <p className="text-yellow-400 text-xs font-medium mb-2">{c.slide8.subtitle}</p>
        
        <div className="space-y-1.5 w-full max-w-sm">
          {c.slide8.sources.map((source, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-gray-800/50 border border-gray-700 rounded-lg p-2.5 flex items-center gap-2"
            >
              <div className="w-6 h-6 rounded bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <Star className="w-3.5 h-3.5 text-yellow-500" />
              </div>
              <div>
                <p className="text-white text-xs font-medium">{source.name}</p>
                <p className="text-gray-500 text-xs">{source.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-teal-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide8.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 9 - Self Assessment (Interactive)
  const renderSlide9 = () => {
    const allAnswered = Object.values(selfAssessment).every(v => v !== null);
    
    return (
      <motion.div 
        key="slide9"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        <div className="flex-1 flex flex-col px-4 py-3 overflow-y-auto">
          <h1 className="text-lg md:text-xl font-bold text-white mb-1 text-center">{c.slide9.title}</h1>
          <p className="text-gray-400 text-xs mb-3 text-center">{c.slide9.intro}</p>
          
          <div className="space-y-3 w-full max-w-sm mx-auto">
            {c.slide9.questions.map((question, i) => (
              <div key={question.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-2.5">
                <p className="text-white text-xs mb-2">{question.text}</p>
                <div className="flex gap-1">
                  {c.slide9.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleAssessmentChange(question.id, option.value)}
                      className={`flex-1 py-1.5 px-1 rounded text-xs transition-all ${
                        selfAssessment[question.id] === option.value
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      {option.value}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-gray-500 text-xs text-center mt-3 italic">{c.slide9.note}</p>
        </div>
        
        <div className="flex-shrink-0 flex justify-center pb-44">
          <button
            onClick={nextScreen}
            disabled={!allAnswered}
            className={`px-5 py-2.5 font-bold rounded-xl transition-all flex items-center gap-2 text-sm ${
              allAnswered
                ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-400 hover:to-teal-500'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            {c.slide9.button}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  };

  // Slide 10 - Improving Your Value Capture
  const renderSlide10 = () => (
    <motion.div 
      key="slide10"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center px-4 py-3 overflow-y-auto">
        <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-2">
          <TrendingUp className="w-5 h-5 text-emerald-500" />
        </div>
        
        <h1 className="text-lg md:text-xl font-bold text-white mb-1 text-center">{c.slide10.title}</h1>
        <p className="text-gray-400 text-xs mb-3 text-center">{c.slide10.intro}</p>
        
        <div className="space-y-1.5 w-full max-w-sm">
          {c.slide10.strategies.map((strategy, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-gray-800/50 border border-gray-700 rounded-lg p-2.5 flex items-center gap-2"
            >
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <IconComponent name={strategy.icon} className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-white text-xs">{strategy.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-teal-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide10.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 11 - Build Your Plan (Interactive)
  const renderSlide11 = () => (
    <motion.div 
      key="slide11"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col px-4 py-3 overflow-y-auto">
        <h1 className="text-lg md:text-xl font-bold text-white mb-1 text-center">{c.slide11.title}</h1>
        <p className="text-gray-400 text-xs mb-3 text-center">{c.slide11.intro}</p>
        
        {/* Area Selection */}
        <p className="text-gray-300 text-xs mb-1.5">{c.slide11.areaLabel}</p>
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {c.slide11.areas.map((area) => (
            <button
              key={area.id}
              onClick={() => handlePlanChange('underCaptureArea', area.id)}
              className={`p-2 rounded-lg border text-left transition-all ${
                capturePlan.underCaptureArea === area.id
                  ? 'bg-emerald-500/20 border-emerald-500'
                  : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
              }`}
            >
              <p className={`text-xs font-medium ${
                capturePlan.underCaptureArea === area.id ? 'text-emerald-400' : 'text-white'
              }`}>{area.label}</p>
              <p className="text-gray-500 text-xs">{area.desc}</p>
            </button>
          ))}
        </div>
        
        {/* Actions Selection */}
        <p className="text-gray-300 text-xs mb-1.5">{c.slide11.actionsLabel}</p>
        <div className="grid grid-cols-2 gap-1.5">
          {c.slide11.actions.map((action) => (
            <button
              key={action.id}
              onClick={() => toggleAction(action.id)}
              className={`p-2 rounded-lg border text-left transition-all ${
                capturePlan.actions.includes(action.id)
                  ? 'bg-emerald-500/20 border-emerald-500'
                  : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                  capturePlan.actions.includes(action.id)
                    ? 'bg-emerald-500 border-emerald-500'
                    : 'border-gray-600'
                }`}>
                  {capturePlan.actions.includes(action.id) && (
                    <CheckCircle2 className="w-3 h-3 text-white" />
                  )}
                </div>
                <p className={`text-xs ${
                  capturePlan.actions.includes(action.id) ? 'text-emerald-400' : 'text-white'
                }`}>{action.label}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          disabled={!capturePlan.underCaptureArea}
          className={`px-5 py-2.5 font-bold rounded-xl transition-all flex items-center gap-2 text-sm ${
            capturePlan.underCaptureArea
              ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-400 hover:to-teal-500'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          {c.slide11.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 12 - Final
  const renderSlide12 = () => {
    const selectedArea = c.slide11.areas.find(a => a.id === capturePlan.underCaptureArea);
    const selectedActions = c.slide11.actions.filter(a => capturePlan.actions.includes(a.id));
    
    return (
      <motion.div 
        key="slide12"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
          <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
            <Target className="w-7 h-7 text-white" />
          </div>
          
          <h1 className="text-xl md:text-2xl font-bold text-white mb-3 text-center">{c.slide12.title}</h1>
          
          <div className="space-y-2 mb-4 max-w-sm text-center">
            <p className="text-gray-300 text-sm">{c.slide12.message}</p>
            <p className="text-emerald-400 font-semibold text-sm">{c.slide12.highlight}</p>
          </div>
          
          <p className="text-gray-400 text-xs mb-4 text-center max-w-sm">{c.slide12.summary}</p>
          
          {/* Plan Summary */}
          {selectedArea && (
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 max-w-sm w-full mb-4">
              <p className="text-gray-400 text-xs mb-1">{lang === 'es' ? 'Área de enfoque:' : 'Focus area:'}</p>
              <p className="text-emerald-400 text-sm font-medium">{selectedArea.label}</p>
              {selectedActions.length > 0 && (
                <>
                  <p className="text-gray-400 text-xs mt-2 mb-1">{lang === 'es' ? 'Acciones:' : 'Actions:'}</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedActions.map(action => (
                      <span key={action.id} className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded text-xs">
                        {action.label}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        
        <div className="flex-shrink-0 flex flex-col items-center gap-2 pb-44">
          <button
            onClick={async () => {
              await savePlan();
              handleFinish();
            }}
            className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-teal-500 transition-all flex items-center gap-2 text-sm"
          >
            <Save className="w-4 h-4" />
            {c.slide12.saveButton}
          </button>
          <button
            onClick={handleFinish}
            className="text-gray-400 text-xs hover:text-white transition-colors"
          >
            {c.slide12.finishButton}
          </button>
        </div>
      </motion.div>
    );
  };

  // Screen renderer
  const screens = [
    renderSlide1,
    renderSlide2,
    renderSlide3,
    renderSlide4,
    renderSlide5,
    renderSlide6,
    renderSlide7,
    renderSlide8,
    renderSlide9,
    renderSlide10,
    renderSlide11,
    renderSlide12
  ];

  return (
    <div className="fixed inset-0 bg-black z-50 overflow-hidden">
      <div className="h-screen flex flex-col">
        {/* Progress bar */}
        <div className="flex-shrink-0 bg-black border-b border-gray-800 px-4 py-2 z-10">
          <div className="max-w-xl mx-auto">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-500 text-xs">
                {lang === 'es' ? 'Captura de Valor' : 'Value Capture'}
              </span>
              <span className="text-gray-500 text-xs">
                {currentScreen + 1} / {screens.length}
              </span>
            </div>
            <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
                initial={{ width: 0 }}
                animate={{ width: `${((currentScreen + 1) / screens.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        </div>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-white transition-colors p-1.5 z-20"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-hidden">
            <div className="w-full max-w-3xl h-full mx-auto">
              <AnimatePresence mode="wait">
                {screens[currentScreen]()}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValueCaptureModule;
